<template>
  <div ref="el" class="market-selector" :class="{ 'is-open': isOpen }">
    <div class="trigger grid-mobile" @click="toggleOpen">
      <div class="back-icon-container">
        <BaseIcon name="fi_arrow-left" class="back-icon" :size="24" color="inherit" />
      </div>

      <div class="trigger-container">
        <BaseFlag :marketLocale="localeMarket" style="color: black" class="flag" />
        <span class="action-large-bold"> {{ localeLanguage }} ({{ localeMarket }}) </span>

        <BaseIcon
          name="fi_chevron-bottom"
          class="trigger-icon"
          :size="16"
          color="inherit"
          :class="{ 'is-open': isOpen }" />
      </div>
    </div>

    <div v-if="isOpen" class="market-items grid-mobile">
      <ul class="items-container">
        <li
          v-for="(locale, index) in validLocales"
          :key="index"
          ref="$locales"
          class="item"
          :class="{ 'is-single-option': locale.languages.length === 1 }"
          @mouseenter="onLocaleItemMouseOver"
          @mouseleave="onLocaleItemMouseOut"
          @click="onLocaleItemClick(locale)">
          <BaseFlag class="item-flag" :marketLocale="locale.id as MarketLocale" />

          <div class="item-content">
            <span class="country action-medium-bold" :class="['is-dark', { 'is-active': localeMarket === locale.id }]">
              {{ locale.label }}
            </span>
            <ul class="variants">
              <li v-for="(language, i) in locale.languages" :key="i" class="language">
                <span v-if="i > 0" class="variant-text"> | </span>
                <NuxtLink
                  :to="getLink(language.id, locale.id)"
                  class="language-text action-medium-regular"
                  :class="{ 'is-active': localeMarket === locale.id && localeLanguage === language.id }">
                  {{ language.label }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BaseFlag, NuxtLink } from '#components';
import gsap from 'gsap';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { Market } from '@seb-platform/shared/types/utils';
import { normalizeUrl } from '@seb-platform/shared/utils/url';
import { BaseIcon } from '~/components/base';
import { useLocale } from '~/composables/useLocale';
import type { Locale, MarketLocale } from '~/types/locales';

const { localeMarket, localeLanguage, locales } = useLocale();

const router = useRouter();

const isOpen = ref(false);

const $el = useTemplateRef('el');

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

function getLink(language: string, market: string) {
  const localeMarket = `${language}_${market}` as Locale;

  let url = '';
  url = normalizeUrl(localeMarket);

  return url;
}

function onLocaleItemMouseOver() {}
function onLocaleItemMouseOut() {}

function onLocaleItemClick(locale: Market) {
  if (locale.languages.length > 1) {
    return;
  }

  const language = locale.languages[0].id;
  const localeId = locale.id;
  const url = getLink(language, localeId);

  void router.push(url);
}

watch(
  () => isOpen.value,
  async () => {
    await nextTick();

    const isMobile = window.innerWidth < 1024;

    if (isOpen.value) {
      const marketItems = $el.value?.querySelector('.market-items');
      if (marketItems instanceof HTMLElement) {
        if (!isMobile) {
          gsap.set(marketItems, { yPercent: -100, y: 30 });
          gsap.to(marketItems, { y: 0, duration: 0.8, ease: 'expo.out', clearProps: 'all' });
        } else {
          gsap.set(marketItems, { autoAlpha: 0 });
          gsap.to(marketItems, { autoAlpha: 1, duration: 0.25, clearProps: 'all' });

          /* items fade */
          const items = marketItems.querySelectorAll('.item');
          gsap.set(items, { x: -30, autoAlpha: 0 });
          gsap.to(items, { delay: 0.1, x: 0, duration: 0.5, autoAlpha: 1, stagger: 0.1, clearProps: true });
        }
      }
    }
  }
);

const validLocales = computed(() => {
  return locales.value.filter(el => el.id !== undefined);
});
</script>

<style src="./BMMarketSelector.scss" lang="scss" scoped />
